import { ComponentState, Modal, Spinner, Text, Title } from "components/common";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { useGetPropGoQrInfoMutation } from "./api";

// Every state (loading, error, code) is laid out on the same centred column of
// this width so the modal keeps one size and the content never shifts as the
// request resolves.
const CONTENT_WIDTH = 288;
const CONTENT_MIN_HEIGHT = 380;

const PropGoPairingModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [getQrInfo, { data, isLoading, isError, error, reset }] =
    useGetPropGoQrInfoMutation();

  // Every open mints a fresh token pair; the payload holds live credentials,
  // so it is dropped from state the moment the modal closes and never
  // persisted or held across navigation.
  useEffect(() => {
    if (open) {
      getQrInfo();
    } else {
      reset();
    }
  }, [open]);

  const qrInfo = data?.qr_info;

  const missingPhone =
    isError && error?.message?.toLowerCase()?.includes("phone number");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      title={t("Pair PropGo Device")}
      width={420}
    >
      <div
        className="d-grid"
        style={{
          placeContent: "center",
          justifyItems: "center",
          textAlign: "center",
          gap: 20,
          minHeight: CONTENT_MIN_HEIGHT,
        }}
      >
        {isLoading ? (
          <Spinner size="large" />
        ) : isError ? (
          <ComponentState
            status="error"
            displayExactMessage
            title={
              missingPhone
                ? t("Phone number required")
                : t("Something went wrong!")
            }
            description={
              missingPhone
                ? t(
                    "Add a phone number to your profile before pairing a PropGo device.",
                  )
                : error?.message
            }
            refetchRequired={!missingPhone}
            onClick={() => getQrInfo()}
            btnLoading={isLoading}
            btnText={t("Retry")}
          />
        ) : qrInfo ? (
          <>
            <Title level={5} style={{ margin: 0 }}>
              {t("Scan this code with the PropGo app")}
            </Title>
            {/* Level H because the app's scanner expects a centre logo occluding
                ~25% of the code; the exact JSON string is what the app parses —
                key order and casing must survive untouched. */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: 8,
                padding: 16,
                width: CONTENT_WIDTH,
                maxWidth: "100%",
                lineHeight: 0,
              }}
            >
              <QRCode
                value={JSON.stringify(qrInfo)}
                level="H"
                size={256}
                viewBox="0 0 256 256"
                style={{ width: "100%", height: "auto", maxWidth: "100%" }}
              />
            </div>
            <Text
              type="secondary"
              style={{
                width: CONTENT_WIDTH,
                maxWidth: "100%",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {t(
                "This code contains a one-time pairing credential. It is regenerated every time you open this window — re-open to get a fresh one.",
              )}
            </Text>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default PropGoPairingModal;
